import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ComplaintDetailResponse,
  ComplaintListFilters,
  ComplaintMutationResponse,
  ComplaintQueueItem,
  ComplaintSeverity,
  ComplaintStatus,
  Localized,
} from '@servicesai/api-client';
import { GlassButton, GlassCard, MetricCard, StatusPill, useI18n } from '@servicesai/ui';
import { admin } from '../../lib/api.js';

type SlaFilter = 'all' | 'due_soon' | 'breached';

const PAGE_SIZE = 20;
const STATUS_OPTIONS: ComplaintStatus[] = [
  'open',
  'triaging',
  'awaiting_review',
  'auto_resolved',
  'escalated',
  'closed',
];
const SEVERITY_OPTIONS: ComplaintSeverity[] = ['low', 'medium', 'high'];

function statusTone(status: ComplaintStatus) {
  if (status === 'closed' || status === 'auto_resolved') return 'published' as const;
  if (status === 'escalated') return 'rejected' as const;
  return 'in_review' as const;
}

function severityClasses(severity: ComplaintSeverity): string {
  if (severity === 'high') return 'border-aered-200 bg-aered-50 text-reject';
  if (severity === 'medium') return 'border-aegold-300 bg-aegold-50 text-gold-deep';
  return 'border-seablue-100 bg-seablue-50 text-oasis';
}

function titleCase(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatCountdown(
  dueAt: string | null,
  now: number,
  lang: 'en' | 'ar',
  settledAt?: string | null,
): { label: string; breached: boolean; dueSoon: boolean } {
  if (!dueAt) {
    return { label: lang === 'ar' ? 'لا يوجد موعد SLA' : 'No SLA due date', breached: false, dueSoon: false };
  }
  const settled = settledAt ? Date.parse(settledAt) : null;
  const comparisonTime = settled !== null && Number.isFinite(settled) ? settled : now;
  const remaining = Date.parse(dueAt) - comparisonTime;
  if (!Number.isFinite(remaining)) {
    return { label: '—', breached: false, dueSoon: false };
  }
  const absoluteMinutes = Math.ceil(Math.abs(remaining) / 60_000);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const duration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  if (remaining < 0) {
    return {
      label: lang === 'ar' ? `متجاوز منذ ${duration}` : `Breached by ${duration}`,
      breached: true,
      dueSoon: false,
    };
  }
  if (settled !== null && Number.isFinite(settled)) {
    return {
      label: lang === 'ar' ? 'تم الرد ضمن مستوى الخدمة' : 'Responded within SLA',
      breached: false,
      dueSoon: false,
    };
  }
  return {
    label: lang === 'ar' ? `متبقٍ ${duration}` : `${duration} remaining`,
    breached: false,
    dueSoon: remaining <= 60 * 60_000,
  };
}

function Badge({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${className}`}>
      {children}
    </span>
  );
}

function QueueCard({
  item,
  selected,
  now,
  onSelect,
}: {
  item: ComplaintQueueItem;
  selected: boolean;
  now: number;
  onSelect: () => void;
}) {
  const { lang } = useI18n();
  const sla = formatCountdown(
    item.sla_due_at,
    now,
    lang,
    item.first_responded_at ?? item.resolved_at,
  );
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={[
        'w-full rounded-xl border p-3 text-start transition',
        selected
          ? 'border-seablue-400 bg-seablue-50 shadow-sm ring-2 ring-seablue-100'
          : 'border-aeblack-100 bg-white hover:border-seablue-200 hover:shadow-sm',
        sla.breached ? 'border-s-4 border-s-aered-500' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] font-bold text-ink-faint">#{item.id.slice(0, 8)}</div>
          <div className="mt-1 line-clamp-2 text-xs font-extrabold text-ink">
            {item.triage_summary || titleCase(item.category ?? item.kind)}
          </div>
        </div>
        <StatusPill state={statusTone(item.status)} label={titleCase(item.status)} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        <Badge className={severityClasses(item.severity)}>{titleCase(item.severity)}</Badge>
        <Badge className="border-aeblack-100 bg-aeblack-50">{titleCase(item.kind)}</Badge>
        <Badge className="border-aeblack-100 bg-white">{titleCase(item.source)}</Badge>
        {item.category && <Badge className="border-aeblack-100 bg-white">{titleCase(item.category)}</Badge>}
      </div>
      <div className={[
        'mt-2 flex items-center justify-between gap-2 text-[10px] font-bold',
        sla.breached ? 'text-reject' : sla.dueSoon ? 'text-gold-deep' : 'text-ink-faint',
      ].join(' ')}>
        <span>{sla.breached ? '● ' : sla.dueSoon ? '◒ ' : '◷ '}{sla.label}</span>
        <span>{item.assigned_to ? (lang === 'ar' ? 'مُسندة' : 'Assigned') : (lang === 'ar' ? 'غير مُسندة' : 'Unassigned')}</span>
      </div>
    </button>
  );
}

export function ComplaintsOperationsPanel() {
  const { lang } = useI18n();
  const text = useCallback((en: string, ar: string) => lang === 'ar' ? ar : en, [lang]);
  const [items, setItems] = useState<ComplaintQueueItem[]>([]);
  const [queueSummary, setQueueSummary] = useState({
    total: 0,
    active: 0,
    breached: 0,
    high: 0,
    escalated: 0,
  });
  const [detail, setDetail] = useState<ComplaintDetailResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [status, setStatus] = useState<ComplaintStatus | ''>('');
  const [severity, setSeverity] = useState<ComplaintSeverity | ''>('');
  const [slaFilter, setSlaFilter] = useState<SlaFilter>('all');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [me, setMe] = useState<{ id: string; roles: string[] } | null>(null);
  const [response, setResponse] = useState<Localized>({ en: '', ar: '' });
  const [reason, setReason] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const loadList = useCallback(async (preferredId?: string | null) => {
    const requestId = ++listRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const filters: ComplaintListFilters = {
        limit: PAGE_SIZE,
        offset,
        ...(status ? { status } : {}),
        ...(severity ? { severity } : {}),
        ...(slaFilter !== 'all' ? { sla: slaFilter } : {}),
      };
      const result = await admin().complaints(filters);
      if (requestId !== listRequestRef.current) return;
      setItems(result.items);
      setQueueSummary(result.summary ?? {
        total: result.items.length,
        active: result.items.filter((item) => !['closed', 'auto_resolved'].includes(item.status)).length,
        breached: result.items.filter((item) => formatCountdown(
          item.sla_due_at,
          Date.now(),
          lang,
          item.first_responded_at ?? item.resolved_at,
        ).breached).length,
        high: result.items.filter((item) => item.severity === 'high').length,
        escalated: result.items.filter((item) => item.status === 'escalated').length,
      });
      const nextId = preferredId && result.items.some((item) => item.id === preferredId)
        ? preferredId
        : result.items[0]?.id ?? null;
      setSelectedId(nextId);
    } catch (reasonValue) {
      if (requestId === listRequestRef.current) {
        setError((reasonValue as Error).message);
      }
    } finally {
      if (requestId === listRequestRef.current) setLoading(false);
    }
  }, [lang, offset, severity, slaFilter, status]);

  const loadDetail = useCallback(async (id: string) => {
    const requestId = ++detailRequestRef.current;
    setDetailLoading(true);
    setError(null);
    try {
      const result = await admin().complaint(id);
      if (
        requestId !== detailRequestRef.current
        || result.complaint.id !== id
      ) return;
      setDetail(result);
      setResponse(result.complaint.response_draft ?? { en: '', ar: '' });
    } catch (reasonValue) {
      if (requestId === detailRequestRef.current) {
        setError((reasonValue as Error).message);
        setDetail(null);
      }
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([
      loadList(),
      admin().me().then((staff) => setMe({ id: staff.id, roles: staff.roles })),
    ]).catch((reasonValue: Error) => setError(reasonValue.message));
  }, [loadList]);

  useEffect(() => {
    detailRequestRef.current += 1;
    setReason('');
    setNotice(null);
    if (selectedId) void loadDetail(selectedId);
    else setDetail(null);
  }, [loadDetail, selectedId]);

  const visibleItems = useMemo(() => items, [items]);
  const metrics = queueSummary;

  const current = detail?.complaint ?? null;
  const events = detail?.events ?? [];
  const isApprover = me?.roles.some((role) => role === 'approver' || role === 'admin') ?? false;
  const canEdit = Boolean(
    current && !['closed', 'auto_resolved'].includes(current.status),
  );
  const responseDirty = Boolean(
    current
    && (
      response.en !== (current.response_draft?.en ?? '')
      || response.ar !== (current.response_draft?.ar ?? '')
    ),
  );

  const mutate = async (
    label: string,
    action: (expectedRevision: number) => Promise<ComplaintMutationResponse>,
  ) => {
    if (!current || acting) return;
    const originalRevision = current.revision;
    setActing(label);
    setError(null);
    setNotice(null);
    setDetail((existing) => existing
      ? { ...existing, complaint: { ...existing.complaint, revision: originalRevision + 1 } }
      : existing);
    setItems((existing) => existing.map((item) =>
      item.id === current.id ? { ...item, revision: originalRevision + 1 } : item));
    try {
      const result = await action(originalRevision);
      setNotice(label);
      if (result.deliveryBlocked) {
        setNotice(text('Delivery blocked by the active-channel policy.', 'تم حظر التسليم وفق سياسة القناة النشطة.'));
      }
      await Promise.all([loadDetail(current.id), loadList(current.id)]);
    } catch (reasonValue) {
      setError((reasonValue as Error).message);
      await Promise.all([loadDetail(current.id), loadList(current.id)]);
    } finally {
      setActing(null);
    }
  };

  const selectCase = (id: string) => {
    setSelectedId(id);
    if (window.matchMedia('(max-width: 1279px)').matches) {
      window.requestAnimationFrame(() => {
        document.getElementById('complaint-case-detail')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  };

  return (
    <section aria-labelledby="complaints-operations-title" className="space-y-4">
      <GlassCard className="overflow-hidden p-0" strong>
        <div className="bg-gradient-to-r from-[#071f2d] via-[#063b57] to-[#00608d] p-5 text-white">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60">
                {text('Citizen experience command', 'قيادة تجربة المتعامل')}
              </div>
              <h2 id="complaints-operations-title" className="mt-1 text-xl font-extrabold">
                {text('Complaints & Feedback Operations', 'عمليات الشكاوى والملاحظات')}
              </h2>
              <p className="mt-1 max-w-3xl text-xs text-white/70">
                {text(
                  'Prioritise SLA risk, review governed triage, and deliver bilingual responses without exposing case bodies in the queue.',
                  'رتّب مخاطر مستوى الخدمة وراجع الفرز المحكوم وقدّم ردوداً ثنائية اللغة دون كشف نصوص الحالات في القائمة.',
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void loadList(selectedId)}
              className="min-h-10 rounded-lg border border-white/25 bg-white/10 px-3 text-xs font-bold text-white backdrop-blur hover:bg-white/20"
            >
              ↻ {text('Refresh queue', 'تحديث القائمة')}
            </button>
          </div>
        </div>
      </GlassCard>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={text('Active in view', 'النشطة في العرض')} value={metrics.active} />
        <MetricCard label={text('SLA breached', 'متجاوزة SLA')} value={metrics.breached} tone={metrics.breached ? 'danger' : 'success'} />
        <MetricCard label={text('High severity', 'عالية الخطورة')} value={metrics.high} tone={metrics.high ? 'warning' : 'neutral'} />
        <MetricCard label={text('Escalated', 'مصعّدة')} value={metrics.escalated} tone={metrics.escalated ? 'warning' : 'neutral'} />
      </div>

      <GlassCard className="p-3">
        <div className="grid gap-2 sm:grid-cols-3">
          <label className="text-[10px] font-bold uppercase text-ink-faint">
            {text('Case status', 'حالة الحالة')}
            <select
              aria-label={text('Case status', 'حالة الحالة')}
              value={status}
              onChange={(event) => { setStatus(event.target.value as ComplaintStatus | ''); setOffset(0); }}
              className="mt-1 min-h-10 w-full rounded-lg border border-aeblack-100 bg-white px-3 text-xs text-ink"
            >
              <option value="">{text('All statuses', 'كل الحالات')}</option>
              {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase text-ink-faint">
            {text('Severity', 'الخطورة')}
            <select
              aria-label={text('Severity', 'الخطورة')}
              value={severity}
              onChange={(event) => { setSeverity(event.target.value as ComplaintSeverity | ''); setOffset(0); }}
              className="mt-1 min-h-10 w-full rounded-lg border border-aeblack-100 bg-white px-3 text-xs text-ink"
            >
              <option value="">{text('All severities', 'كل مستويات الخطورة')}</option>
              {SEVERITY_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
            </select>
          </label>
          <label className="text-[10px] font-bold uppercase text-ink-faint">
            {text('SLA posture', 'وضع مستوى الخدمة')}
            <select
              aria-label={text('SLA posture', 'وضع مستوى الخدمة')}
              value={slaFilter}
              onChange={(event) => {
                setSlaFilter(event.target.value as SlaFilter);
                setOffset(0);
              }}
              className="mt-1 min-h-10 w-full rounded-lg border border-aeblack-100 bg-white px-3 text-xs text-ink"
            >
              <option value="all">{text('All SLA states', 'كل حالات SLA')}</option>
              <option value="due_soon">{text('Due within 60 minutes', 'مستحقة خلال ٦٠ دقيقة')}</option>
              <option value="breached">{text('Breached only', 'المتجاوزة فقط')}</option>
            </select>
          </label>
        </div>
      </GlassCard>

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{error}</div></div>}
      {notice && <div role="status" className="aegov-alert alert-success"><div className="alert-content text-sm">{notice}</div></div>}

      <div className="grid min-h-[42rem] gap-4 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <GlassCard className="flex min-h-0 flex-col p-3">
          <div className="flex items-center justify-between gap-2 px-1 pb-3">
            <div>
              <h3 className="text-sm font-extrabold">{text('Prioritised case queue', 'قائمة الحالات ذات الأولوية')}</h3>
              <p className="text-[10px] text-ink-faint">{text('Bodies remain concealed until selection.', 'تبقى النصوص مخفية حتى اختيار الحالة.')}</p>
            </div>
            <Badge className="border-seablue-100 bg-seablue-50 text-oasis">{visibleItems.length}</Badge>
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pe-1">
            {loading && <div className="space-y-2" aria-busy="true">{[0, 1, 2].map((item) => <div key={item} className="h-28 animate-pulse rounded-xl bg-aeblack-50" />)}</div>}
            {!loading && visibleItems.map((item) => (
              <QueueCard
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                now={now}
                onSelect={() => selectCase(item.id)}
              />
            ))}
            {!loading && visibleItems.length === 0 && (
              <div className="rounded-xl border border-dashed border-aeblack-200 p-8 text-center text-xs text-ink-faint">
                {text('No cases match these filters.', 'لا توجد حالات تطابق هذه المرشحات.')}
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-aeblack-100 pt-3">
            <button type="button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} className="aegov-btn btn-outline btn-xs min-h-9 disabled:opacity-40">
              {text('Previous', 'السابق')}
            </button>
            <span className="font-mono text-[10px] text-ink-faint">
              {items.length === 0 ? '0–0' : `${offset + 1}–${offset + items.length}`} / {queueSummary.total}
            </span>
            <button type="button" disabled={offset + items.length >= queueSummary.total} onClick={() => setOffset(offset + PAGE_SIZE)} className="aegov-btn btn-outline btn-xs min-h-9 disabled:opacity-40">
              {text('Next', 'التالي')}
            </button>
          </div>
        </GlassCard>

        <div id="complaint-case-detail" className="min-w-0 scroll-mt-4">
          {detailLoading && <GlassCard className="h-full min-h-[36rem] animate-pulse bg-aeblack-50 p-5"><span className="sr-only">{text('Loading case detail', 'جارٍ تحميل تفاصيل الحالة')}</span></GlassCard>}
          {!detailLoading && !current && (
            <GlassCard className="grid h-full min-h-[36rem] place-items-center p-8 text-center">
              <div><div className="text-4xl">◫</div><p className="mt-3 text-sm font-bold">{text('Select a case to open the secure detail.', 'اختر حالة لفتح التفاصيل الآمنة.')}</p></div>
            </GlassCard>
          )}
          {!detailLoading && current && (
            <div className="space-y-4">
              <GlassCard className="overflow-hidden p-0" strong>
                <div className="border-b border-aeblack-100 bg-aeblack-50 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-mono text-[10px] text-ink-faint">CASE / {current.id}</div>
                      <h3 className="mt-1 text-lg font-extrabold">{current.triage_summary || titleCase(current.kind)}</h3>
                      <div className="mt-2 flex flex-wrap gap-1">
                        <StatusPill state={statusTone(current.status)} label={titleCase(current.status)} />
                        <Badge className={severityClasses(current.severity)}>{titleCase(current.severity)}</Badge>
                        <Badge className="border-aeblack-100 bg-white">{titleCase(current.kind)}</Badge>
                        <Badge className="border-aeblack-100 bg-white">{titleCase(current.source)}</Badge>
                        {current.category && <Badge className="border-aeblack-100 bg-white">{titleCase(current.category)}</Badge>}
                      </div>
                    </div>
                    <div className={[
                      'rounded-xl border px-4 py-3 text-end',
                      formatCountdown(
                        current.sla_due_at,
                        now,
                        lang,
                        current.first_responded_at ?? current.resolved_at,
                      ).breached
                        ? 'border-aered-200 bg-aered-50 text-reject'
                        : 'border-aegold-200 bg-aegold-50 text-gold-deep',
                    ].join(' ')}>
                      <div className="text-[9px] font-bold uppercase">{text('SLA clock', 'ساعة SLA')}</div>
                      <div className="mt-1 font-mono text-sm font-extrabold">
                        {formatCountdown(
                          current.sla_due_at,
                          now,
                          lang,
                          current.first_responded_at ?? current.resolved_at,
                        ).label}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 p-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(16rem,0.8fr)]">
                  <div>
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-extrabold">{text('Decrypted citizen statement', 'نص المتعامل بعد فك التشفير')}</h4>
                      <span className="text-[9px] font-bold uppercase text-ink-faint">{text('Detail view only', 'عرض التفاصيل فقط')}</span>
                    </div>
                    <blockquote className="mt-2 rounded-xl border-s-4 border-s-seablue-400 bg-seablue-50 p-4 text-sm leading-7 text-ink">
                      {current.body || text('No statement supplied.', 'لم يتم توفير نص.')}
                    </blockquote>
                  </div>
                  <div className="space-y-3">
                    <div className="rounded-xl border border-aeblack-100 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-[10px] font-extrabold uppercase text-ink-faint">{text('Triage confidence', 'ثقة الفرز')}</h4>
                        <span className="font-mono text-xs font-extrabold">{Math.round(Number(current.triage_confidence ?? 0) * 100)}%</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-aeblack-100"><div className="h-full rounded-full bg-seablue-500" style={{ width: `${Math.max(0, Math.min(100, Number(current.triage_confidence ?? 0) * 100))}%` }} /></div>
                    </div>
                    <div className={[
                      'rounded-xl border p-3',
                      current.risk_flags.length ? 'border-aered-200 bg-aered-50' : 'border-aeblack-100 bg-white',
                    ].join(' ')}>
                      <h4 className="text-[10px] font-extrabold uppercase text-ink-faint">{text('Risk rationale', 'مبررات المخاطر')}</h4>
                      <p className="mt-1 text-xs font-bold">
                        {current.risk_flags.length
                          ? current.risk_flags.map(titleCase).join(' · ')
                          : text('No elevated legal, safety, privacy, fraud, or discrimination flag.', 'لا توجد إشارة مرتفعة للمخاطر القانونية أو السلامة أو الخصوصية أو الاحتيال أو التمييز.')}
                      </p>
                      {current.urgency && <p className="mt-1 text-[10px] text-ink-faint">{text('Urgency', 'الاستعجال')}: {titleCase(current.urgency)}</p>}
                    </div>
                  </div>
                </div>
              </GlassCard>

              <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.3fr)_minmax(19rem,0.7fr)]">
                <GlassCard className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-extrabold">{text('Bilingual response studio', 'استوديو الرد ثنائي اللغة')}</h4>
                      <p className="text-[10px] text-ink-faint">{text('Both variants are required and versioned together.', 'كلا النصين مطلوب ويتم إصدار نسختهما معاً.')}</p>
                    </div>
                    <StatusPill state={current.response_status === 'sent' ? 'published' : current.response_status === 'failed' ? 'rejected' : 'in_review'} label={titleCase(current.response_status)} />
                  </div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <label className="text-[10px] font-bold uppercase text-ink-faint">
                      {text('English response', 'الرد بالإنجليزية')}
                      <textarea
                        aria-label={text('English response', 'الرد بالإنجليزية')}
                        rows={7}
                        value={response.en}
                        disabled={!canEdit}
                        onChange={(event) => setResponse((value) => ({ ...value, en: event.target.value }))}
                        className="mt-1 w-full resize-y rounded-xl border border-aeblack-100 bg-white p-3 text-sm font-normal leading-6 text-ink focus:border-seablue-400 focus:outline-none disabled:bg-aeblack-50"
                        dir="ltr"
                      />
                    </label>
                    <label className="text-[10px] font-bold uppercase text-ink-faint">
                      {text('Arabic response', 'الرد بالعربية')}
                      <textarea
                        aria-label={text('Arabic response', 'الرد بالعربية')}
                        rows={7}
                        value={response.ar}
                        disabled={!canEdit}
                        onChange={(event) => setResponse((value) => ({ ...value, ar: event.target.value }))}
                        className="mt-1 w-full resize-y rounded-xl border border-aeblack-100 bg-white p-3 text-sm font-normal leading-6 text-ink focus:border-seablue-400 focus:outline-none disabled:bg-aeblack-50"
                        dir="rtl"
                      />
                    </label>
                  </div>
                  <label className="mt-3 block text-[10px] font-bold uppercase text-ink-faint">
                    {text('Audited action reason', 'سبب الإجراء المسجل')}
                    <input
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      maxLength={1_000}
                      placeholder={text('Optional operational reason', 'سبب تشغيلي اختياري')}
                      className="mt-1 min-h-10 w-full rounded-lg border border-aeblack-100 bg-white px-3 text-xs font-normal text-ink"
                    />
                  </label>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <GlassButton
                      variant="ghost"
                      disabled={!canEdit || acting !== null || !response.en.trim() || !response.ar.trim()}
                      onClick={() => void mutate(text('Draft saved', 'تم حفظ المسودة'), (revision) => admin().updateComplaintResponse(current.id, response, revision))}
                    >
                      {text('Save draft', 'حفظ المسودة')}
                    </GlassButton>
                    {isApprover && current.response_status === 'drafted' && (
                      <GlassButton
                        variant="primary"
                        disabled={acting !== null || !response.en.trim() || !response.ar.trim()}
                        onClick={() => void mutate(text('Response approved', 'تم اعتماد الرد'), (revision) => admin().approveComplaintResponse(current.id, revision, response))}
                      >
                        {text('Approve response', 'اعتماد الرد')}
                      </GlassButton>
                    )}
                    {isApprover && current.response_status === 'approved' && (
                      <GlassButton
                        variant="primary"
                        disabled={acting !== null || responseDirty}
                        title={responseDirty
                          ? text(
                              'Save and approve the visible edits before sending.',
                              'احفظ التعديلات الظاهرة واعتمدها قبل الإرسال.',
                            )
                          : undefined}
                        onClick={() => void mutate(text('Response queued for delivery', 'تمت جدولة الرد للتسليم'), (revision) => admin().sendComplaintResponse(current.id, revision))}
                      >
                        {text('Send response', 'إرسال الرد')}
                      </GlassButton>
                    )}
                  </div>
                </GlassCard>

                <GlassCard className="p-4">
                  <h4 className="text-sm font-extrabold">{text('Case timeline', 'الخط الزمني للحالة')}</h4>
                  <div className="mt-3 max-h-[28rem] space-y-0 overflow-y-auto">
                    {events.map((event, index) => (
                      <div key={event.id} className="relative ps-6 pb-5 last:pb-0">
                        {index < events.length - 1 && <span className="absolute start-[7px] top-4 h-full w-px bg-aeblack-100" />}
                        <span className="absolute start-0 top-1 grid h-4 w-4 place-items-center rounded-full bg-seablue-500 text-[8px] text-white">✓</span>
                        <div className="text-xs font-extrabold">{titleCase(event.event)}</div>
                        <div className="mt-0.5 text-[10px] text-ink-faint">
                          {event.actor_kind} · {new Date(event.created_at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
                        </div>
                      </div>
                    ))}
                    {events.length === 0 && <p className="text-xs text-ink-faint">{text('No recorded events.', 'لا توجد أحداث مسجلة.')}</p>}
                  </div>
                </GlassCard>
              </div>

              <GlassCard className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-extrabold">{text('Governed case actions', 'إجراءات الحالة المحكومة')}</h4>
                    <p className="text-[10px] text-ink-faint">{text(`Optimistic revision ${current.revision}`, `الإصدار المتفائل ${current.revision}`)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {current.status !== 'closed' && (
                      <button
                        type="button"
                        disabled={acting !== null}
                        onClick={() => void mutate(
                          current.assigned_to === me?.id ? text('Case unassigned', 'تم إلغاء إسناد الحالة') : text('Case assigned to you', 'تم إسناد الحالة إليك'),
                          (revision) => admin().assignComplaint(current.id, current.assigned_to === me?.id ? null : me?.id ?? null, revision),
                        )}
                        className="aegov-btn btn-outline btn-sm min-h-10"
                      >
                        {current.assigned_to === me?.id ? text('Unassign', 'إلغاء الإسناد') : text('Assign to me', 'إسناد إليّ')}
                      </button>
                    )}
                    {current.status !== 'closed' && current.status !== 'escalated' && (
                      <button type="button" disabled={acting !== null} onClick={() => void mutate(text('Case escalated', 'تم تصعيد الحالة'), (revision) => admin().escalateComplaint(current.id, revision, reason || 'staff_request'))} className="aegov-btn btn-outline btn-sm min-h-10 border-aegold-300 text-gold-deep">
                        {text('Escalate', 'تصعيد')}
                      </button>
                    )}
                    {current.status !== 'closed' && (
                      <button type="button" disabled={acting !== null} onClick={() => void mutate(text('Case closed', 'تم إغلاق الحالة'), (revision) => admin().closeComplaint(current.id, revision, reason || 'staff_request'))} className="aegov-btn btn-outline btn-sm min-h-10">
                        {text('Close', 'إغلاق')}
                      </button>
                    )}
                    {isApprover && (current.status === 'closed' || current.status === 'auto_resolved') && (
                      <button type="button" disabled={acting !== null} onClick={() => void mutate(text('Case reopened', 'أُعيد فتح الحالة'), (revision) => admin().reopenComplaint(current.id, revision, reason || 'staff_request'))} className="aegov-btn btn-outline btn-sm min-h-10">
                        {text('Reopen', 'إعادة الفتح')}
                      </button>
                    )}
                  </div>
                </div>
              </GlassCard>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

interface ComplaintMixRow {
  status?: string;
  category?: string;
  severity?: string;
  kind?: string;
  source?: string;
  count: number;
}

interface ComplaintTrendRow {
  day: string;
  count: number;
  sla_breaches: number;
  auto_replied: number;
  escalated: number;
}

interface ComplaintsReportData {
  processedThisRun?: number;
  failedThisRun?: number;
  ingestedSurveys?: number;
  classificationsWithLlm?: number;
  routeCounts?: { auto_send?: number; awaiting_review?: number; escalate?: number };
  counts?: { total?: number; active?: number; sla_breaches?: number; auto_replied?: number; escalated?: number };
  rates?: { autoReplyPct?: number; escalationPct?: number };
  mix?: {
    status?: ComplaintMixRow[];
    category?: ComplaintMixRow[];
    severity?: ComplaintMixRow[];
    kind?: ComplaintMixRow[];
    source?: ComplaintMixRow[];
  };
  trend30Days?: ComplaintTrendRow[];
  reportWindowDays?: number;
}

function MixBars({ rows, field }: { rows: ComplaintMixRow[]; field: 'category' | 'severity' | 'kind' | 'source' }) {
  const maximum = Math.max(1, ...rows.map((row) => Number(row.count)));
  return (
    <div className="space-y-2">
      {rows.slice(0, 8).map((row, index) => (
        <div key={`${row[field] ?? index}-${index}`}>
          <div className="mb-1 flex justify-between gap-2 text-[10px]"><span className="font-bold">{titleCase(row[field] ?? 'unclassified')}</span><span className="font-mono">{row.count}</span></div>
          <div className="h-1.5 overflow-hidden rounded-full bg-aeblack-100"><div className="h-full rounded-full bg-seablue-500" style={{ width: `${(Number(row.count) / maximum) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export function ComplaintsReportVisualization({ data }: { data: unknown }) {
  const { lang } = useI18n();
  const text = (en: string, ar: string) => lang === 'ar' ? ar : en;
  const report = (data && typeof data === 'object' ? data : {}) as ComplaintsReportData;
  const counts = report.counts ?? {};
  const trend = Array.isArray(report.trend30Days) ? report.trend30Days : [];
  const maxTrend = Math.max(1, ...trend.map((row) => Number(row.count)));
  const points = trend.map((row, index) => {
    const x = trend.length <= 1 ? 0 : (index / (trend.length - 1)) * 600;
    const y = 130 - (Number(row.count) / maxTrend) * 110;
    return `${x},${y}`;
  }).join(' ');
  const routes = [
    { key: 'auto_send', value: report.routeCounts?.auto_send ?? 0, color: 'bg-aegreen-500' },
    { key: 'awaiting_review', value: report.routeCounts?.awaiting_review ?? 0, color: 'bg-aegold-500' },
    { key: 'escalate', value: report.routeCounts?.escalate ?? 0, color: 'bg-aered-500' },
  ];
  const routeTotal = Math.max(1, routes.reduce((sum, route) => sum + route.value, 0));

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        {[
          [text('Total cases', 'إجمالي الحالات'), counts.total ?? 0],
          [text('Active', 'النشطة'), counts.active ?? 0],
          [text('SLA breaches', 'تجاوزات SLA'), counts.sla_breaches ?? 0],
          [text('Auto-replied', 'رد آلي'), counts.auto_replied ?? 0],
          [text('Escalated', 'مصعّدة'), counts.escalated ?? 0],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-xl border border-aeblack-100 bg-white p-3">
            <div className="text-[10px] font-bold uppercase text-ink-faint">{label}</div>
            <div className="mt-1 font-mono text-xl font-extrabold">{value}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.5fr)_minmax(18rem,0.7fr)]">
        <div className="rounded-xl border border-aeblack-100 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-xs font-extrabold">
              {text(
                `${report.reportWindowDays ?? 30}-day intake trend`,
                `اتجاه الاستلام خلال ${report.reportWindowDays ?? 30} يوماً`,
              )}
            </h4>
            <span className="text-[10px] text-ink-faint">{trend.reduce((sum, row) => sum + Number(row.count), 0)} {text('cases', 'حالة')}</span>
          </div>
          {trend.length > 0 ? (
            <svg viewBox="0 0 600 145" role="img" aria-label={text('Complaint intake trend', 'اتجاه استلام الشكاوى')} className="mt-2 h-36 w-full">
              <defs><linearGradient id="complaint-trend-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0173ab" stopOpacity=".25" /><stop offset="100%" stopColor="#0173ab" stopOpacity="0" /></linearGradient></defs>
              <path d={`M0,130 L${points.replaceAll(' ', ' L')} L600,130 Z`} fill="url(#complaint-trend-fill)" />
              <polyline points={points} fill="none" stroke="#0173ab" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : <p className="py-12 text-center text-xs text-ink-faint">{text('No trend observations.', 'لا توجد بيانات اتجاه.')}</p>}
        </div>
        <div className="rounded-xl border border-aeblack-100 bg-white p-4">
          <h4 className="text-xs font-extrabold">{text('Routing decisions', 'قرارات التوجيه')}</h4>
          <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-aeblack-100">
            {routes.map((route) => <div key={route.key} className={route.color} style={{ width: `${(route.value / routeTotal) * 100}%` }} />)}
          </div>
          <div className="mt-4 space-y-2">
            {routes.map((route) => <div key={route.key} className="flex items-center justify-between text-xs"><span>{titleCase(route.key)}</span><b className="font-mono">{route.value}</b></div>)}
          </div>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {([
          ['category', text('Category mix', 'مزيج الفئات'), report.mix?.category ?? []],
          ['severity', text('Severity mix', 'مزيج الخطورة'), report.mix?.severity ?? []],
          ['kind', text('Feedback kind', 'نوع الملاحظات'), report.mix?.kind ?? []],
          ['source', text('Intake source', 'مصدر الاستلام'), report.mix?.source ?? []],
        ] as const).map(([field, label, rows]) => (
          <div key={field} className="rounded-xl border border-aeblack-100 bg-white p-4">
            <h4 className="mb-3 text-xs font-extrabold">{label}</h4>
            <MixBars rows={rows} field={field} />
          </div>
        ))}
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        <div className="rounded-lg bg-aeblack-50 p-3 text-xs"><span className="text-ink-faint">{text('Processed', 'تمت المعالجة')}</span><b className="float-end font-mono">{report.processedThisRun ?? 0}</b></div>
        <div className="rounded-lg bg-aeblack-50 p-3 text-xs"><span className="text-ink-faint">{text('Failed', 'فشلت')}</span><b className="float-end font-mono">{report.failedThisRun ?? 0}</b></div>
        <div className="rounded-lg bg-aeblack-50 p-3 text-xs"><span className="text-ink-faint">{text('Survey intake', 'استلام الاستبيانات')}</span><b className="float-end font-mono">{report.ingestedSurveys ?? 0}</b></div>
        <div className="rounded-lg bg-aeblack-50 p-3 text-xs"><span className="text-ink-faint">{text('AI-assisted', 'بمساعدة الذكاء')}</span><b className="float-end font-mono">{report.classificationsWithLlm ?? 0}</b></div>
      </div>
    </div>
  );
}
